import { ReactNode } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import type {
  EventLayout,
  EventRenderContext,
  TimeGridEntity,
  TimeGridEvent,
  CardAppearance
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { TimeGridRowEvents } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/components/TimeGridRowEvents'

interface Props<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent, TDragDrop> {
  entity: TEntity
  rowHeaderWidth: number
  minColumnWidth: number
  gridMinWidth: number
  paddingX: number
  paddingY: number
  cardLayout: {
    minHeight: number
    paddingX: number
    paddingY: number
    radius: number
  }
  showGridLines: boolean
  columnCount: number
  layouts: EventLayout<TEvent>[]
  rowHeader: ReactNode
  rowActions?: ReactNode
  eventRenderer: (event: TEvent, context: EventRenderContext<TDragDrop>) => ReactNode
  cardAppearance?: (event: TEvent) => CardAppearance
  dragDropHandlers?: TDragDrop
  secondaryLayer?: ReactNode
  rowRef?: (node: HTMLDivElement | null) => void
  selectedSlots?: Set<number>
  onCellSelected?: (
    slotIndex: number,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void
  isRowSelected?: boolean
  onRowHeaderSelected?: () => void
}

const TimeGridRow = <TEntity extends TimeGridEntity, TEvent extends TimeGridEvent, TDragDrop>({
  entity: _entity,
  rowHeaderWidth,
  minColumnWidth,
  gridMinWidth,
  paddingX,
  paddingY,
  cardLayout,
  showGridLines,
  columnCount,
  layouts,
  rowHeader,
  rowActions,
  eventRenderer,
  cardAppearance,
  dragDropHandlers,
  secondaryLayer,
  rowRef,
  selectedSlots,
  onCellSelected,
  isRowSelected = false,
  onRowHeaderSelected
}: Props<TEntity, TEvent, TDragDrop>) => {
  const handleCellClick = (event: MouseEvent) => {
    if (!onCellSelected || columnCount <= 0) return

    const currentTarget = event.currentTarget
    if (!(currentTarget instanceof HTMLDivElement)) return

    const bounds = currentTarget.getBoundingClientRect()
    if (bounds.width <= 0) return

    const relativeX = event.clientX - bounds.left
    const slotWidth = bounds.width / columnCount
    const computedIndex = Math.floor(relativeX / slotWidth)
    const slotIndex = Math.max(0, Math.min(columnCount - 1, computedIndex))
    onCellSelected(slotIndex, {
      shiftKey: event.shiftKey,
      metaKey: event.metaKey,
      ctrlKey: event.ctrlKey
    })
  }

  const handleRowHeaderClick = () => {
    onRowHeaderSelected?.()
  }

  return (
    <Box
      role="row"
      ref={rowRef}
      style={{
        display: 'grid',
        gridTemplateColumns: `${rowHeaderWidth}px repeat(${columnCount}, minmax(${minColumnWidth}px, 1fr))`,
        minWidth: `${gridMinWidth}px`,
        alignItems: 'stretch'
      }}
    >
      <Box
        role="rowheader"
        background="grey000White"
        onClick={handleRowHeaderClick}
        border={
          showGridLines
            ? {
                bottom: { width: 's1', style: 'solid', color: 'grey300' },
                right: { width: 's1', style: 'solid', color: 'grey300' }
              }
            : undefined
        }
        style={{
          position: 'relative',
          left: '0',
          zIndex: '1',
          display: 'flex',
          gap: '0',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          boxSizing: 'border-box',
          overflow: 'visible',
          cursor: onRowHeaderSelected ? 'pointer' : undefined,
          userSelect: 'none'
        }}
      >
        {isRowSelected ? (
          <Box
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: '0',
              boxShadow: 'inset 0 0 0 2px #2B74FF',
              pointerEvents: 'none',
              zIndex: '2'
            }}
          />
        ) : null}
        <Box
          style={{
            display: 'flex',
            alignItems: 'stretch',
            width: '100%',
            height: '100%'
          }}
        >
          {rowHeader}
        </Box>
        {rowActions ?? null}
      </Box>
      <Box
        role="gridcell"
        onClick={handleCellClick}
        border={
          showGridLines ? { bottom: { width: 's1', style: 'solid', color: 'grey300' } } : undefined
        }
        style={{
          gridColumn: `2 / span ${columnCount}`,
          position: 'relative',
          overflow: 'visible',
          cursor: onCellSelected ? 'pointer' : undefined,
          userSelect: 'none'
        }}
      >
        {selectedSlots && selectedSlots.size > 0 ? (
          <Box
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: '0',
              display: 'grid',
              gridTemplateColumns: `repeat(${columnCount}, minmax(${minColumnWidth}px, 1fr))`,
              pointerEvents: 'none',
              zIndex: '1'
            }}
          >
            {Array.from({ length: columnCount }).map((_, index) => (
              <Box
                key={`selected-cell-${index}`}
                style={{
                  boxShadow: selectedSlots.has(index) ? 'inset 0 0 0 2px #2B74FF' : undefined
                }}
              />
            ))}
          </Box>
        ) : null}
        {showGridLines ? (
          <Box
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: '0',
              display: 'grid',
              gridTemplateColumns: `repeat(${columnCount}, minmax(${minColumnWidth}px, 1fr))`,
              pointerEvents: 'none'
            }}
          >
            {/* Grid overlay for vertical column separators. */}
            {Array.from({ length: columnCount }).map((_, index) => (
              <Box
                key={`grid-line-${index}`}
                flexGrow={false}
                border={{ right: { width: 's1', style: 'solid', color: 'grey300' } }}
              />
            ))}
          </Box>
        ) : null}
        {secondaryLayer ? (
          <Box
            style={{
              position: 'absolute',
              inset: '0',
              pointerEvents: 'none'
            }}
          >
            {/* Secondary layer is rendered behind events (e.g., background markers). */}
            {secondaryLayer}
          </Box>
        ) : null}
        <TimeGridRowEvents
          layouts={layouts}
          paddingX={paddingX}
          paddingY={paddingY}
          cardLayout={cardLayout}
          minColumnWidth={minColumnWidth}
          columnCount={columnCount}
          eventRenderer={eventRenderer}
          cardAppearance={cardAppearance}
          dragDropHandlers={dragDropHandlers}
        />
      </Box>
    </Box>
  )
}

export { TimeGridRow }
